import { ListRequest } from '@weave/schema-gen-ts/dist/schemas/brand-settings/v1/service.pb';
import {
  BrandSetting,
  SettingType_Enum as SettingType,
} from '@weave/schema-gen-ts/dist/schemas/brand-settings/v1/settings.pb';
import { LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { getListQueryKey, useListQuery } from './queries/use-list';
import { ListIO } from './types';

export const queryKeys = {
  list: (request: UseListBrandSettingsRequest) =>
    getListQueryKey({ ...request, settingType: SettingType.BRAND_SETTING }),
} as const;

export type BrandSettingsByLocation = Record<string, BrandSetting>;
type ListQueryKey = ReturnType<typeof getListQueryKey>;
// Omit 'select' and 'queryKey' to prevent callers from breaking the hook's return type contract
type ListQueryOptions = Omit<
  LimitedSchemaQueryOptions<ListIO, unknown, BrandSettingsByLocation, ListQueryKey>,
  'select' | 'queryKey' | 'retry'
>;

type UseListBrandSettingsRequest = {
  orgId: string;
  locationIds: string[];
};

type UseListBrandSettingsParams = UseListBrandSettingsRequest & {
  queryOptions?: ListQueryOptions;
};

/**
 * A hook that retrieves brand settings for multiple locations.
 *
 * @param orgId - The organization ID to fetch brand settings for.
 * @param locationIds - Array of location IDs to fetch brand settings for.
 *
 * @returns React Query result with `data` as `Record<string, BrandSetting>` where keys are location IDs.
 */
export const useListBrandSettings = ({ orgId, locationIds, queryOptions }: UseListBrandSettingsParams) => {
  const enabled = queryOptions?.enabled;

  const request: ListRequest = {
    locationIds,
    orgId,
    settingType: SettingType.BRAND_SETTING,
  };

  return useListQuery(request, {
    select: (data: ListIO['output']) => {
      return (
        data?.settings.reduce((acc: BrandSettingsByLocation, item: ListIO['output']['settings'][number]) => {
          if (item.setting && 'brandSetting' in item.setting) {
            // TODO: need to check with the BE to see if this is planned to be fixed on the BE (i.e. locationId is not an empty string on the response)
            acc[item.locationId] = { ...item.setting.brandSetting, locationId: item.locationId };
          }
          return acc;
        }, {} as BrandSettingsByLocation) ?? {}
      );
    },
    ...queryOptions,
    enabled: (enabled ?? true) && !!orgId && locationIds.length > 0,
    retry: 0,
  });
};
